// Interaction engine (7.6D; moved from the Studio to @interactads/creative-runtime
// in 7.6H, ADR-0028): deterministic execution of the declarative interaction
// model, on a logical clock. ONE engine for both modes: the Studio preview
// (RUNTIME-CONTRACT §15 "editor/preview": no network, no instance, no event
// sent) and the served runtime (which owns the network, the instance and the
// transport of the events recorded here). The engine itself never does I/O.
// It follows INTERACTION-MODEL §8:
//
// - one queue of trigger occurrences, run-to-completion, derived triggers
//   (scene.enter/exit, variable.changed) enqueued and processed after, cascade
//   depth ≤ 8; rules in document order, each condition evaluated just before
//   its rule runs; `once`/`cooldownMs` checked before conditions;
// - time is logical: `advance(dt)` processes timeline instants, scene ends,
//   timers and `after` actions IN CHRONOLOGICAL ORDER (ties: timeline, timers,
//   deferred actions), so the same inputs at the same instants always give the
//   same state and the same preview events;
// - a rule is reachable only while its target is on screen (current scene or
//   overlay, present in the variant, visible, inside its timing window);
// - `after` actions are cancelled when their scene is left;
// - scene transitions (ADR-0020 §5, ST-O12 accepted): one logically active
//   scene at a time. The outgoing scene stops accepting input and its
//   deferred actions are cancelled when the transition BEGINS (scene.exit);
//   both scenes are only drawn for the effect; the incoming scene becomes
//   active and interactable, its timeline starts and scene.enter fires when
//   the transition COMPLETES. "none" (or 0 ms) is immediate. A scene change
//   requested during a transition is applied right after it completes (the
//   latest request wins), so timers/interactions never create two active scenes;
// - randomness: a seeded PRNG (never Math.random).
//
// Preview events mirror the standard taxonomy names (scene_view, cta_click,
// element_interaction, engagement, custom; since 7.6E media, carousel, form
// and survey events) for the author to see what WOULD be measured. None of
// them is billable; `creative_loaded` is not simulated.
//
// Advanced components (7.6E), on the same logical clock:
// - media: the engine is the source of truth (position advances with logical
//   time; milestones 25/50/75 and the end are scheduled instants). Authored
//   intent vs outcome is explicit: `withSound` autoplay starts MUTED (browsers
//   require a gesture for sound), and the "block autoplay" simulation turns
//   any autoplay into media.blocked + a tap-to-play state. Media of a scene
//   stop and rewind when the scene is left (transition start); autoplay runs
//   when a scene becomes active (transition completion). video_start /
//   milestones / *_complete are measured once per instance (replays emit
//   media_control{replay}); the real <video>/<audio> elements only FOLLOW;
// - carousel: active index per carousel; auto-advance on the clock, held
//   while hovered/focused or paused, stopped for good by the first user
//   navigation; reset when its scene is entered; a slide view is measured
//   once per slide per instance (no flood from looping auto-advance);
// - survey: the graph is walked exactly as declared (answers are ephemeral,
//   never stored); entering its scene starts it; leaving before completion
//   fires survey.exited; completion goes to the completion scene;
// - forms: values live only in this preview; submit validates required
//   fields and measures only option ids and scale values.
// Advergaming (7.6F, ADR-0022): the declared game module runs through the
// Creative Engine's GameSession (shared with 7.6H) — this engine only applies
// its effects on the logical clock: score/lives via the declared variables,
// element states, seeded relocation/shuffle positions, the mismatch settle,
// game.event occurrences (input-caused ones keep user activation), the result
// scene after its delay, game_start / aggregated game_interaction /
// game_complete / restart. A tap on a target or card is decided by the game
// BEFORE the tap's own rules. Draggable layers drop by their center (pointer)
// or by pick-and-place (keyboard). One logical interaction runs at most
// ENGINE_LIMITS.actionsPerInteraction actions (cascade ≤ 8, bounded deferred
// actions); the excess is cut and reported once (runtime_error).
// Runner / platformer (7.6F.1, ADR-0023): the Creative Engine's RunnerSession
// (inside the GameSession) is advanced by fixed steps of RUNNER_STEP_MS that
// are SCHEDULED INSTANTS of this engine's logical clock, in chronological
// order with timers and deferred actions — so a slower display rate runs the
// same steps and gets the same contacts. The host (player) clamps one frame's
// catch-up to RUNNER_MAX_CATCH_UP_MS. Leaving the game's scenes pauses the
// steps; restart resets the run. Jump/duck are user gestures (keyboard,
// on-screen controls); contacts are derived occurrences. Frame-by-frame
// positions are never CreativeOperations nor events: the renderer reads them
// from the session; React only hears about discrete changes (pose, segment,
// objects appearing/leaving).
// Pure logic: the sampler, the expression evaluator and the game session are the
// Creative Engine's (@interactads/creative-model), shared by every host.

import {
  ENGINE_LIMITS,
  RUNNER_STEP_MS,
  SURVEY_END,
  createGameSession,
  createRng,
  evaluateExpr,
  inTimingWindow,
  randomPlacement,
  rectCenter,
  resolveVariant,
  snapToCenter,
  zoneAt,
  type Action,
  type GameEffect,
  type GameOutcome,
  type GameSession,
  type GameSignal,
  type RunnerSession,
  type Rect,
  type CarouselProps,
  type CreativeDocument,
  type Element,
  type SurveyQuestion,
  type ExprEnvironment,
  type ExprValue,
  type Rule,
  type Scene,
  type Trigger,
} from "@interactads/creative-model";

export type PreviewEventType =
  | "scene_view"
  | "cta_click"
  | "element_interaction"
  | "engagement"
  | "custom"
  | "video_start"
  | "video_25"
  | "video_50"
  | "video_75"
  | "video_complete"
  | "audio_start"
  | "audio_complete"
  | "media_control"
  | "carousel_slide_view"
  | "carousel_navigate"
  | "form_start"
  | "form_interaction"
  | "form_submit"
  | "survey_start"
  | "survey_question_view"
  | "answer"
  | "survey_complete"
  // 7.6F
  | "game_start"
  | "game_interaction"
  | "game_complete"
  | "restart"
  | "runtime_error";

export interface PreviewEvent {
  readonly t: number;
  readonly type: PreviewEventType;
  readonly attrs: Readonly<Record<string, string | number | readonly string[] | Readonly<Record<string, number>>>>;
}

/** Media playback state (logical): what the real element is made to follow. */
export interface MediaView {
  readonly playing: boolean;
  readonly muted: boolean;
  readonly volume: number;
  /** Position in ms at the current logical instant. */
  readonly position: number;
  readonly duration: number;
  readonly ended: boolean;
  /** Autoplay was refused (simulated policy, or the real browser in the served ad): tap-to-play state. */
  readonly blocked: boolean;
  /** Served mode: autoplay requested from the browser, not started yet (the element is asked to play). */
  readonly pending: boolean;
  /** Never played in this instance (poster shown). */
  readonly fresh: boolean;
  /**
   * Served mode only (7.6J): the real element cannot load or decode this media (MediaError,
   * NotSupportedError). Final for the instance: never played, never measured, poster shown.
   */
  readonly failed: boolean;
  /** Authored intent vs outcome, when they differ (shown to the author). */
  readonly outcome?: "muted_instead_of_sound" | "autoplay_blocked";
}

export interface CarouselView {
  readonly index: number;
  readonly count: number;
  readonly autoplay: boolean;
  readonly paused: boolean;
}

export interface SurveyView {
  readonly started: boolean;
  readonly completed: boolean;
  readonly question: SurveyQuestion | null;
  /** Option order as shown (shuffled with the instance RNG when the question asks for it). */
  readonly order: readonly string[];
  /** 1-based position of the current question on this participant's path. */
  readonly position: number;
  /** Longest remaining path including the current question (progress denominator). */
  readonly remaining: number;
  readonly selection: readonly string[];
  readonly value: number | null;
}

interface MediaState {
  playing: boolean;
  muted: boolean;
  volume: number;
  pos: number;
  anchor: number;
  started: boolean;
  fired: Set<number>;
  completed: boolean;
  ended: boolean;
  blocked: boolean;
  loops: number;
  outcome?: MediaView["outcome"];
  /**
   * Served mode (mediaStart "confirmed"): an autoplay the BROWSER has not started yet. The
   * logical clock of the media does not run until the real element reports playback
   * (mediaPlaying) or the browser refuses it (mediaBlocked).
   */
  pending: boolean;
  /** Served mode: the browser cannot load/decode the media (see MediaView.failed). */
  failed: boolean;
}

interface CarouselState {
  index: number;
  since: number;
  autoplay: boolean;
  held: boolean;
  paused: boolean;
  viewed: Set<number>;
}

interface SurveyState {
  started: boolean;
  completed: boolean;
  current: string | null;
  path: string[];
  answers: Map<string, { opts?: string[]; value?: number }>;
  selection: string[];
  value: number | null;
  order: string[];
}

interface FormState {
  started: boolean;
  values: Map<string, string[] | number | string>;
  completed: Set<string>;
  errors: Set<string>;
}

const MILESTONES = [25, 50, 75] as const;

/** Local free text kept per field at most (never measured, never sent). */
export const LOCAL_TEXT_MAX = 280;

/**
 * A field value restricted to what the field declares (7.6I hardening): option ids only for
 * choice controls, an integer inside the scale for ratings, bounded plain text for local text.
 * undefined = rejected (a tampered DOM control can never inject another value).
 */
export function fieldValueOf(
  decl: NonNullable<CreativeDocument["forms"]>[number]["fields"][number],
  value: string[] | number | string
): string[] | number | string | undefined {
  switch (decl.control) {
    case "radio":
    case "select":
      return typeof value === "string" && (value === "" || decl.options.some((o) => o.id === value))
        ? value
        : undefined;
    case "checkbox":
      return Array.isArray(value) && value.every((v) => decl.options.some((o) => o.id === v))
        ? [...new Set(value)]
        : undefined;
    case "rating":
      return typeof value === "number" && Number.isInteger(value) && value >= 1 && value <= decl.scale.max
        ? value
        : undefined;
    case "text":
    case "textarea": {
      if (typeof value !== "string") return undefined;
      const max = Math.min(decl.validation?.maxLength ?? LOCAL_TEXT_MAX, LOCAL_TEXT_MAX);
      return value.slice(0, max);
    }
  }
}

export interface SceneTransitionState {
  readonly from: string;
  readonly kind: "fade" | "slide" | "push";
  readonly direction: "left" | "right" | "up" | "down";
  readonly duration: number;
  readonly easing: NonNullable<Scene["transitionIn"]>["easing"];
  readonly start: number;
  /** Scene time of the scene being left when it was left (to draw it as it was). */
  readonly fromTime: number;
}

interface TimerState {
  running: boolean;
  /** Logical time at which the current cycle started. */
  cycleStart: number;
  /** Elapsed time of the current cycle when stopped. */
  pausedAt: number;
  cycles: number;
  /** timer.tick rules: elapsed time (in the current cycle) of the last tick fired, per rule. */
  ticks: Map<string, number>;
}

interface Pending {
  readonly due: number;
  readonly actions: readonly Action[];
  readonly scene: string;
  readonly seq: number;
  readonly depth: number;
}

type Occurrence = { readonly trigger: Trigger; readonly depth: number; readonly gesture: boolean };
type SceneCause = "user" | "timeline" | "survey" | "game";

/** What the renderer needs to draw and announce a layer that plays a role in the game. */
export interface GameElementView {
  readonly role: "target" | "card" | "item" | "zone";
  /** One-time target already found. */
  readonly found?: boolean;
  readonly face?: "down" | "up" | "matched";
  /** Drop item placed for good (in this zone). */
  readonly placedIn?: string;
  /** Keyboard pick-and-place: this item is picked up. */
  readonly picked?: boolean;
}

export interface GameNotice {
  readonly t: number;
  readonly event: GameSignal;
  readonly target?: string;
}

/**
 * Aggregation window of game_interaction (RUNTIME-CONTRACT §8.3: counts per window, never one per tap).
 * Since runtime 1.1.2 a window opens at the first counted interaction and closes on its own after this
 * much logical time (scheduled in `advance`), at the game end / restart, or by `flushPending()`.
 */
export const GAME_INTERACTION_WINDOW_MS = 5_000;

/**
 * A rule's trigger matches an occurrence when every parameter the rule names is
 * equal; parameters it leaves out are wildcards (e.g. `survey.answered` without
 * `question` = any question, `drag.drop` without `onto`).
 */
const sameTrigger = (rule: Trigger, occurrence: Trigger) => {
  const o = occurrence as unknown as Record<string, unknown>;
  return Object.entries(rule).every(([k, v]) => JSON.stringify(v) === JSON.stringify(o[k]));
};

export interface EngineOptions {
  /** Session seed of the instance RNG (shuffles, random placement, random values). Same seed ⇒ same session. */
  readonly seed?: number;
  /**
   * Simulated browser autoplay policy: "allow-muted" (typical: muted autoplay
   * allowed, sound needs a gesture) or "block" (no autoplay at all).
   */
  readonly autoplay?: "allow-muted" | "block";
  /** Called when a link.open runs (the preview shows the destination, never navigates). */
  readonly onLink?: (linkId: string, url: string | undefined) => void;
  /**
   * When an AUTOPLAY counts as started (7.6I). "logical" (Studio preview, default): at once, on
   * the logical clock. "confirmed" (served ad): only when the real browser element reports
   * playback (`mediaPlaying`); a refusal (`mediaBlocked`) becomes the tap-to-play state. So
   * video_start / audio_start and the milestones never describe a playback the browser refused.
   * Plays caused by a user gesture start at once in both modes.
   */
  readonly mediaStart?: "logical" | "confirmed";
}

export class InteractionEngine {
  readonly doc: CreativeDocument;
  readonly layoutId: string;
  /** Logical time since the creative started (ms). */
  now = 0;
  scene: string;
  sceneStart = 0;
  /** The current scene reached its timeline end and holds (no loop / go-to). */
  sceneEnded = false;
  transition: SceneTransitionState | null = null;
  /** Incremented on every DISCRETE change (scene, visibility, variables…): the renderer re-renders on it. */
  version = 0;
  readonly events: PreviewEvent[] = [];
  /** Action types met that the preview does not execute (media, survey, game…), reported once each. */
  readonly unsupported = new Set<string>();
  readonly #visibility = new Map<string, boolean>();
  readonly #vars = new Map<string, ExprValue>();
  readonly #states = new Map<string, string>();
  readonly #timers = new Map<string, TimerState>();
  readonly #manual = new Map<string, Record<string, number>>();
  readonly #enabled = new Map<string, boolean>();
  readonly #onceFired = new Set<string>();
  readonly #lastFired = new Map<string, number>();
  readonly #timelineFired = new Set<string>();
  readonly #customCount = new Map<string, number>();
  #pending: Pending[] = [];
  #seq = 0;
  #engaged = false;
  /** Scene change requested while a transition runs (applied at its completion; latest wins). */
  #deferredGoTo: { sceneId: string; transition: Scene["transitionIn"] | undefined; cause: SceneCause } | null = null;
  #enterCause: SceneCause = "user";
  #enterFrom = "";
  readonly #media = new Map<string, MediaState>();
  readonly #carousels = new Map<string, CarouselState>();
  readonly #forms = new Map<string, FormState>();
  readonly #survey: SurveyState = {
    started: false,
    completed: false,
    current: null,
    path: [],
    answers: new Map(),
    selection: [],
    value: null,
    order: [],
  };
  /** Child → parent (group or component slot) for nested layers (carousel slides). */
  readonly #parents = new Map<string, { parent: string; slot?: string }>();
  readonly #autoplay: NonNullable<EngineOptions["autoplay"]>;
  readonly #mediaStart: NonNullable<EngineOptions["mediaStart"]>;
  /** creative.visibility (7.6I): last visibility reported by the host (undefined = never reported). */
  #visible: boolean | undefined;
  readonly #random: () => number;
  readonly #onLink: EngineOptions["onLink"];
  readonly #layer: Map<string, string[]>;
  readonly #presentation: ReturnType<typeof resolveVariant>;
  // ─── Game (7.6F) ───
  readonly seed: number;
  readonly game: GameSession | null;
  /** Game started/ended flags of a rule-only game (no module): game_start / game_complete once per round. */
  #roundStarted = false;
  #roundEnded = false;
  /** The game's time limit was paused because the active scene left the game. */
  #gamePaused = false;
  /** Runtime position (top-left, variant coordinates) of layers moved by the game: relocation, shuffle, snap, drag. */
  readonly #positions = new Map<string, { x: number; y: number }>();
  #settleAt: number | null = null;
  #resultNav: { due: number; scene: string } | null = null;
  #picked: string | null = null;
  #dragging: string | null = null;
  readonly #interactions = { tap: 0, drag: 0 };
  #windowStart = 0;
  readonly gameLog: GameNotice[] = [];
  /** Runner contacts of the current round (author-facing preview status; reset by restart). */
  readonly runnerStats = { hazards: 0, pickups: 0 };
  /** Logical instant of the runner's next fixed step (null = not stepping: idle, paused, ended). */
  #runnerNext: number | null = null;
  #runnerSeen = -1;
  // ─── Execution budget (INTERACTION-MODEL §8.4) ───
  #runDepth = 0;
  #actions = 0;
  readonly #reported = new Set<string>();

  constructor(doc: CreativeDocument, layoutId: string, options: EngineOptions = {}) {
    this.doc = doc;
    this.layoutId = layoutId;
    this.scene = doc.initialSceneId;
    this.seed = options.seed ?? 1;
    this.#random = createRng(this.seed);
    this.#onLink = options.onLink;
    this.#autoplay = options.autoplay ?? "allow-muted";
    this.#mediaStart = options.mediaStart ?? "logical";
    for (const el of doc.elements) {
      if (el.type === "group") for (const c of el.children) this.#parents.set(c, { parent: el.id });
      if (el.type === "component")
        for (const [slot, list] of Object.entries(el.slots ?? {}))
          for (const c of list) this.#parents.set(c, { parent: el.id, slot });
    }
    this.#presentation = resolveVariant(doc, layoutId);
    this.#layer = new Map(Object.entries(this.#presentation?.scenes ?? {}).map(([k, v]) => [k, [...v]]));
    for (const v of doc.variables ?? []) this.#vars.set(v.id, v.initial);
    for (const el of doc.elements) if (el.initialState) this.#states.set(el.id, el.initialState);
    this.game = createGameSession(doc, layoutId);
  }

  // ─── Queries (renderer) ──────────────────────────────────────────────────

  currentScene(): Scene {
    return (this.doc.scenes.find((s) => s.id === this.scene) ?? this.doc.scenes[0]) as Scene;
  }

  /** Scene time: holds at the duration once the timeline ended. */
  sceneTime(): number {
    // During a transition the incoming scene is drawn at 0: its timeline starts at completion.
    const t = Math.max(0, this.now - this.sceneStart);
    const d = this.currentScene().duration;
    return d !== null && (this.sceneEnded || t > d) ? d : t;
  }

  /** Visibility after show/hide actions (undefined = the element's own initial visibility). */
  visibilityOverride(id: string): boolean | undefined {
    return this.#visibility.get(id);
  }
  visibilityOverrides(): ReadonlyMap<string, boolean> {
    return this.#visibility;
  }
  variables(): ReadonlyMap<string, ExprValue> {
    return this.#vars;
  }
  manualStarts(): ReadonlyMap<string, Readonly<Record<string, number>>> {
    return this.#manual;
  }
  timerRemaining(id: string): number {
    const value = this.#env().timer(id, "remainingMs");
    return typeof value === "number" ? value : 0;
  }
  variable(id: string): ExprValue | undefined {
    return this.#vars.get(id);
  }

  /** On screen right now (current scene or overlay, in the variant, visible, in its window). */
  isOnScreen(id: string): boolean {
    // No scene accepts input during a transition.
    if (this.transition) return false;
    const nested = this.#parents.get(id);
    if (nested) {
      // A nested layer is on screen with its parent; a carousel slide only while it is the active one.
      if (nested.slot === "slides") {
        const slides = this.#element(nested.parent);
        const list = slides?.type === "component" ? (slides.slots?.slides ?? []) : [];
        if (list.indexOf(id) !== this.carouselView(nested.parent).index) return false;
      }
      const p = this.#presentation?.elements[id] as { visible?: boolean } | undefined;
      return (this.#visibility.get(id) ?? p?.visible !== false) && this.isOnScreen(nested.parent);
    }
    const inScene =
      (this.#layer.get(this.scene) ?? []).includes(id) ||
      (this.#presentation?.overlay.below ?? []).includes(id) ||
      (this.#presentation?.overlay.above ?? []).includes(id);
    if (!inScene) return false;
    const p = this.#presentation?.elements[id] as { visible?: boolean } | undefined;
    const visible = this.#visibility.get(id) ?? p?.visible !== false;
    const el = this.doc.elements.find((e) => e.id === id);
    return visible && inTimingWindow(el?.timing, this.sceneTime());
  }

  /** Current element state (element.setState / game found & matched states); undefined = base presentation. */
  stateOf(id: string): string | undefined {
    // The runner's player shows the declared state of its pose (jump / duck / hit), if any.
    const r = this.game?.runner;
    if (r && r.decl.player.element === id) return r.poseState() ?? this.#states.get(id);
    return this.#states.get(id);
  }
  /** Runtime top-left of a layer moved by the game (variant coordinates); undefined = its authored frame. */
  positionOf(id: string): { readonly x: number; readonly y: number } | undefined {
    return this.#positions.get(id);
  }
  /** Authored frame of a top-level layer in this variant. */
  #frame(id: string): Rect | undefined {
    const f = (this.#presentation?.elements[id] as { frame?: Rect } | undefined)?.frame;
    return f ? { x: f.x, y: f.y, width: f.width, height: f.height } : undefined;
  }
  /** Frame where the layer is now (authored frame at its runtime position). */
  frameNow(id: string): Rect | undefined {
    const f = this.#frame(id);
    const p = this.#positions.get(id);
    return f && p ? { ...f, x: p.x, y: p.y } : f;
  }

  /** The runner of the declared game (positions, pose, instances for the renderer); null = no runner. */
  get runner(): RunnerSession | null {
    return this.game?.runner ?? null;
  }
  /** The runner accepts jump/duck now (running, in a game scene, not paused, no transition). */
  runnerActive(): boolean {
    const g = this.game;
    return (
      g?.runner !== null &&
      g?.running === true &&
      !this.#gamePaused &&
      !this.transition &&
      g.decl.scenes.includes(this.scene)
    );
  }

  /** Role and live state of a layer in the declared game (null = not part of the game). */
  gameView(id: string): GameElementView | null {
    const g = this.game;
    if (!g) return null;
    if (g.isCard(id)) return { role: "card", face: g.cardFace(id) };
    if (g.isTarget(id)) return { role: "target", found: g.isFound(id) };
    if (g.isItem(id)) {
      const placedIn = g.placedZone(id);
      return { role: "item", ...(placedIn ? { placedIn } : {}), picked: this.#picked === id };
    }
    if (g.isZone(id)) return { role: "zone" };
    return null;
  }
  /** Readable game state for the preview's status line. */
  gameStatus(): {
    readonly phase: "idle" | "running" | "ended" | "paused";
    readonly outcome: GameOutcome | null;
  } | null {
    const g = this.game;
    if (!g) return null;
    return { phase: g.running && this.#gamePaused ? "paused" : g.phase, outcome: g.outcome };
  }
  /** Item picked with the keyboard (pick-and-place alternative to dragging). */
  get picked(): string | null {
    return this.#picked;
  }

  #rulesFor(type: Trigger["type"], target: string) {
    return (this.doc.rules ?? []).filter(
      (r) => r.on.type === type && "target" in r.on && r.on.target === target && this.#isEnabled(r)
    );
  }
  /** The game handles a tap on this layer right now (running, in a game scene, not already done). */
  #gameTappable(id: string): boolean {
    const g = this.game;
    if (!g?.running || this.#gamePaused || !g.decl.scenes.includes(this.scene)) return false;
    if (g.isCard(id)) return g.cardFace(id) === "down";
    if (g.isTarget(id)) return !g.isFound(id);
    // Keyboard pick-and-place: zones accept the picked item.
    if (g.isZone(id)) return this.#picked !== null;
    // Runner on-screen controls.
    const c = g.runner?.decl.controls;
    if (c && (c.jump === id || c.duck === id)) return this.runnerActive();
    return false;
  }
  /** Whether a tap on the element would run something (cursor, keyboard focus). */
  isTappable(id: string): boolean {
    return this.#rulesFor("pointer.tap", id).length > 0 || this.#gameTappable(id);
  }
  hasHover(id: string): boolean {
    return this.#rulesFor("pointer.hover.enter", id).length > 0 || this.#rulesFor("pointer.hover.leave", id).length > 0;
  }

  // ─── Inputs ───────────────────────────────────────────────────────────────

  /** Starts the creative: timers with autoStart, the initial scene. */
  start(): void {
    for (const t of this.doc.timers ?? []) if (t.autoStart) this.#startTimer(t.id);
    this.#log("scene_view", { scene: this.scene, cause: "start" });
    const derived: Occurrence[] = [];
    this.#enterScene(this.scene, derived, 0);
    this.#run([
      { trigger: { type: "creative.ready" }, depth: 0, gesture: false },
      { trigger: { type: "scene.enter", scene: this.scene }, depth: 0, gesture: false },
      ...derived,
    ]);
    this.#changed();
  }

  /**
   * The host reports whether the creative is visible (7.6I; served ad only — the Studio preview
   * is always visible and never calls it). Fires `creative.visibility` rules on each CHANGE
   * (the first report fires only when the creative starts hidden). Informative trigger: it
   * never affects measurement (INTERACTION-MODEL §3; per-environment REQUIRES VERIFICATION).
   */
  setVisible(visible: boolean): void {
    const previous = this.#visible ?? true;
    this.#visible = visible;
    if (previous === visible) return;
    this.#run([{ trigger: { type: "creative.visibility", visible }, depth: 0, gesture: false }]);
  }

  tap(id: string): void {
    if (!this.isOnScreen(id)) return;
    // Keyboard pick-and-place: a tap on a zone (or Enter on it) drops the picked item there.
    if (this.#picked !== null && this.game?.isZone(id)) {
      this.place(id);
      return;
    }
    const rules = this.#rulesFor("pointer.tap", id);
    const game = this.#gameTappable(id);
    const opens = rules.some((r) => JSON.stringify(r.do).includes('"link.open"'));
    if (!opens && rules.length > 0 && !game) this.#log("element_interaction", { el: id, kind: "tap" });
    if (rules.length > 0 || game) this.#engage();
    // The game decides first (score, found, pair…), so the tap's own rules see the result
    // (e.g. a quiz's last answer runs game.end auto with the updated score).
    const derived: Occurrence[] = [];
    const controls = this.game?.runner?.decl.controls;
    if (game && controls && (controls.jump === id || controls.duck === id)) {
      // A control tap (click, Enter): a jump, or a duck pressed and released (its minimum duration).
      const g = this.game;
      const fx = controls.jump === id ? g.runnerJump() : [...g.runnerDuck(true), ...g.runnerDuck(false)];
      this.#applyGame(fx, derived, 0);
    } else if (game) {
      const g = this.game as GameSession;
      const fx = g.isCard(id) ? g.tapCard(id) : g.tapTarget(id);
      this.#applyGame(fx, derived, 0);
    }
    this.#run([{ trigger: { type: "pointer.tap", target: id }, depth: 0, gesture: true }, ...derived]);
  }

  // ─── Runner inputs (7.6F.1): gestures from the keyboard and on-screen controls ─

  /** Jump (Space / ↑ / the jump control). Ignored unless the runner is active. */
  runnerJump(): void {
    if (!this.runnerActive()) return;
    this.#runnerInput((this.game as GameSession).runnerJump());
  }
  /** Duck pressed / released (↓ held / the duck control held). */
  runnerDuck(pressed: boolean): void {
    if (!this.runnerActive()) {
      if (!pressed) this.game?.runnerDuck(false);
      return;
    }
    this.#runnerInput((this.game as GameSession).runnerDuck(pressed));
  }
  #runnerInput(fx: GameEffect[]): void {
    if (fx.length === 0) return;
    this.#engage();
    const derived: Occurrence[] = [];
    this.#applyGame(fx, derived, 0);
    this.#run(derived);
  }

  /** One fixed simulation step (a scheduled instant): contacts become derived occurrences. */
  #runnerStep(): void {
    const g = this.game;
    if (!g?.runner || this.#runnerNext === null) return;
    this.#runnerNext += RUNNER_STEP_MS;
    const fx = g.runnerStep(this.#random);
    if (fx.length > 0) {
      const derived: Occurrence[] = [];
      this.#applyGame(fx, derived, 0);
      this.#run(derived);
    } else {
      // Finishing the course needs no effect: check the end after every step.
      const outcome = g.running ? g.checkEnd(this.#readings()) : null;
      if (outcome) this.#endGame(outcome);
    }
    if (g.runner.version !== this.#runnerSeen) {
      this.#runnerSeen = g.runner.version;
      this.#changed();
    }
  }

  // ─── Drag and drop (draggable behavior; game drop items) ────────────────

  #draggable(id: string) {
    const el = this.#element(id);
    const b = el?.behaviors?.find((x) => x.type === "draggable");
    return b?.type === "draggable" ? b : undefined;
  }
  /** Can be dragged now: draggable, on screen, not placed; game items only while the game runs. */
  isDraggable(id: string): boolean {
    if (!this.#draggable(id) || !this.isOnScreen(id)) return false;
    const g = this.game;
    if (g?.isItem(id))
      return g.running && !this.#gamePaused && g.placedZone(id) === undefined && g.decl.scenes.includes(this.scene);
    return true;
  }

  /** Clamps a top-left position of the layer to its drag bounds and axis. */
  #dragBounds(id: string, x: number, y: number): { x: number; y: number } {
    const f = this.#frame(id);
    const b = this.#draggable(id);
    if (!f || !b) return { x, y };
    const size = this.#canvas();
    const axis = b.axis ?? "both";
    const nx = axis === "y" ? (this.frameNow(id)?.x ?? f.x) : Math.max(0, Math.min(size.width - f.width, x));
    const ny = axis === "x" ? (this.frameNow(id)?.y ?? f.y) : Math.max(0, Math.min(size.height - f.height, y));
    return { x: Math.round(nx), y: Math.round(ny) };
  }

  #canvas(): { width: number; height: number } {
    const l = this.doc.layouts.find((x) => x.id === this.layoutId);
    if (!l) return { width: 1, height: 1 };
    return l.sizing.mode === "fixed"
      ? { width: l.sizing.width, height: l.sizing.height }
      : { width: l.sizing.design.width, height: l.sizing.design.height };
  }

  /** Pointer drag begins (gesture). Returns false when the layer cannot be dragged now. */
  dragStart(id: string): boolean {
    if (!this.isDraggable(id)) return false;
    this.#picked = null;
    this.#dragging = id;
    this.#engage();
    this.#run([{ trigger: { type: "pointer.drag.start", target: id }, depth: 0, gesture: true }]);
    return true;
  }

  /** Where the dragged layer would be drawn for a pointer-proposed top-left (bounds and axis applied). */
  dragPosition(id: string, x: number, y: number): { x: number; y: number } {
    return this.#dragBounds(id, x, y);
  }

  /** Pointer released with the layer's top-left at (x, y) in variant coordinates. */
  dragEnd(id: string, x: number, y: number): void {
    if (this.#dragging !== id) return;
    this.#dragging = null;
    const pos = this.#dragBounds(id, x, y);
    this.#drop(id, pos);
  }

  /** Keyboard alternative: pick an item up (Enter/Space on it). */
  pick(id: string): void {
    if (!this.isDraggable(id)) return;
    this.#picked = this.#picked === id ? null : id;
    if (this.#picked) this.#engage();
    this.#changed();
  }
  cancelPick(): void {
    if (this.#picked === null) return;
    this.#picked = null;
    this.#changed();
  }
  /** Keyboard alternative: drop the picked item onto a zone (as if released over its center). */
  place(zone: string): void {
    const id = this.#picked;
    const f = id ? this.#frame(id) : undefined;
    const z = this.frameNow(zone);
    this.#picked = null;
    if (!id || !f || !z || !this.isDraggable(id)) {
      this.#changed();
      return;
    }
    this.#drop(id, snapToCenter(f, z));
  }

  /** Zones a drop can land on, back-to-front: the game's zones and the item's declared drop targets. */
  #dropZones(id: string): Array<{ id: string; rect: Rect }> {
    const ids = new Set([...(this.game?.decl.drops?.zones ?? []), ...(this.#draggable(id)?.dropTargets ?? [])]);
    const order = [
      ...(this.#presentation?.overlay.below ?? []),
      ...(this.#layer.get(this.scene) ?? []),
      ...(this.#presentation?.overlay.above ?? []),
    ];
    return order.flatMap((z) => {
      const rect = ids.has(z) && z !== id && this.isOnScreen(z) ? this.frameNow(z) : undefined;
      return rect ? [{ id: z, rect }] : [];
    });
  }

  #drop(id: string, pos: { x: number; y: number }): void {
    const f = this.#frame(id);
    const b = this.#draggable(id);
    if (!f || !b) return;
    const center = rectCenter({ ...f, x: pos.x, y: pos.y });
    const zone = zoneAt(center, this.#dropZones(id));
    const accepts = b.dropTargets ?? [];
    const derived: Occurrence[] = [];
    this.#log("element_interaction", { el: id, kind: "drop" });
    if (this.game?.isItem(id)) {
      // The session decides: snap (accepted) or return; a drop it ignores (round over) returns too.
      const fx = this.game.drop(id, zone, accepts);
      if (fx.length === 0) this.#positions.delete(id);
      this.#applyGame(fx, derived, 0);
    } else if (zone !== null && accepts.includes(zone)) {
      this.#positions.set(id, pos);
    } else if (b.snapBack !== false) {
      this.#positions.delete(id);
    } else this.#positions.set(id, pos);
    const onto = zone !== null && accepts.includes(zone) ? zone : undefined;
    this.#run([
      { trigger: { type: "pointer.drag.end", target: id }, depth: 0, gesture: true },
      ...(onto !== undefined
        ? [{ trigger: { type: "drag.drop" as const, target: id, onto }, depth: 0, gesture: true }]
        : []),
      ...derived,
    ]);
  }

  hover(id: string, enter: boolean): void {
    if (!this.isOnScreen(id)) return;
    const type = enter ? "pointer.hover.enter" : "pointer.hover.leave";
    if (this.#rulesFor(type, id).length === 0) return;
    this.#log("element_interaction", { el: id, kind: "hover" });
    this.#engage();
    this.#run([{ trigger: { type, target: id }, depth: 0, gesture: false }]);
  }

  /** Advances logical time, processing every scheduled occurrence in chronological order. */
  advance(dt: number): void {
    const target = this.now + Math.max(0, dt);
    for (let guard = 0; guard < 10_000; guard++) {
      const next = this.#nextScheduled(target);
      if (!next) break;
      this.now = next.at;
      next.run();
    }
    this.now = target;
  }

  // ─── Scheduling ─────────────────────────────────────────────────────────

  #nextScheduled(limit: number): { at: number; run: () => void } | null {
    const candidates: Array<{ at: number; order: number; run: () => void }> = [];
    const scene = this.currentScene();
    const tr = this.transition;
    if (tr)
      candidates.push({
        at: tr.start + tr.duration,
        order: -1,
        run: () => {
          this.#completeTransition();
        },
      });
    if (!this.sceneEnded && !tr) {
      for (const r of this.doc.rules ?? []) {
        if (r.on.type !== "timeline.at" || r.on.scene !== scene.id || this.#timelineFired.has(r.id)) continue;
        const at = this.sceneStart + r.on.atMs;
        const rule = r;
        candidates.push({
          at,
          order: 0,
          run: () => {
            this.#timelineFired.add(rule.id);
            this.#run([{ trigger: rule.on, depth: 0, gesture: false }], [rule]);
          },
        });
      }
      if (scene.duration !== null)
        candidates.push({
          at: this.sceneStart + scene.duration,
          order: 1,
          run: () => {
            this.#sceneEnd(scene);
          },
        });
    }
    for (const [id, t] of this.#timers) {
      const decl = this.doc.timers?.find((x) => x.id === id);
      if (!t.running || !decl) continue;
      // timer.tick: every `everyMs` of the running cycle (ticks up to and including its end).
      for (const r of this.doc.rules ?? []) {
        if (r.on.type !== "timer.tick" || r.on.timer !== id) continue;
        const elapsed = (t.ticks.get(r.id) ?? 0) + r.on.everyMs;
        if (elapsed > decl.durationMs) continue;
        const rule = r;
        candidates.push({
          at: t.cycleStart + elapsed,
          order: 1.9,
          run: () => {
            t.ticks.set(rule.id, elapsed);
            this.#run([{ trigger: rule.on, depth: 0, gesture: false }], [rule]);
          },
        });
      }
      candidates.push({
        at: t.cycleStart + decl.durationMs,
        order: 2,
        run: () => {
          this.#timerElapsed(id);
        },
      });
    }
    if (!tr) {
      for (const [id, m] of this.#media) {
        if (!m.playing) continue;
        const d = this.#duration(id);
        const pos = this.#position(m, d);
        for (const milestone of MILESTONES) {
          const at = (milestone / 100) * d;
          if (m.fired.has(milestone) || pos > at) continue;
          candidates.push({
            at: this.now + (at - pos),
            order: 2.4 + milestone / 1_000,
            run: () => {
              this.#milestone(id, milestone);
            },
          });
        }
        candidates.push({
          at: this.now + (d - pos),
          order: 2.5,
          run: () => {
            this.#mediaEnd(id);
          },
        });
      }
      for (const [id, c] of this.#carousels) {
        const ms = this.#carouselProps(id)?.autoAdvanceMs ?? 0;
        if (!c.autoplay || c.held || c.paused || ms <= 0 || !this.isOnScreen(id)) continue;
        candidates.push({
          at: c.since + ms,
          order: 2.6,
          run: () => {
            this.#autoAdvance(id);
          },
        });
      }
    }
    // Runner: the next fixed step (only while the round runs in a game scene, never during a transition).
    if (this.#runnerNext !== null && !tr && this.runnerActive())
      candidates.push({
        at: this.#runnerNext,
        order: 2.65,
        run: () => {
          this.#runnerStep();
        },
      });
    // Game: the end of a mismatch delay, and the result scene after the outcome.
    if (this.#settleAt !== null)
      candidates.push({
        at: this.#settleAt,
        order: 2.7,
        run: () => {
          this.#settleAt = null;
          this.game?.settleMismatch();
          this.#changed();
        },
      });
    // game_interaction: an open window closes on its own after GAME_INTERACTION_WINDOW_MS of logical
    // time (runtime 1.1.2) — it never waits for another tap.
    if (this.#hasPendingInteractions())
      candidates.push({
        at: Math.max(this.now, this.#windowStart + GAME_INTERACTION_WINDOW_MS),
        order: 2.75,
        run: () => {
          this.#flushInteractions();
        },
      });
    const nav = this.#resultNav;
    if (nav)
      candidates.push({
        at: nav.due,
        order: 2.8,
        run: () => {
          this.#resultNav = null;
          this.#goTo(nav.scene, undefined, "game", 0);
        },
      });
    for (const p of this.#pending)
      candidates.push({
        at: p.due,
        order: 3 + p.seq / 1e9,
        run: () => {
          this.#runPending(p);
        },
      });
    const due = candidates.filter((c) => c.at <= limit && c.at >= this.now);
    due.sort((a, b) => a.at - b.at || a.order - b.order);
    return due[0] ?? null;
  }

  #sceneEnd(scene: Scene): void {
    this.#run([{ trigger: { type: "scene.timelineEnd", scene: scene.id }, depth: 0, gesture: false }]);
    if (this.scene !== scene.id) return; // a rule already moved on
    const end = scene.onTimelineEnd ?? "hold";
    if (end === "hold") {
      this.sceneEnded = true;
      this.#changed();
    } else if (end === "loop") {
      this.sceneStart = this.now;
      this.#timelineFired.clear();
      this.#manual.clear();
      this.#changed();
    } else this.#goTo(end.goTo, undefined, "timeline", 0);
  }

  #completeTransition(): void {
    this.transition = null;
    this.#log("scene_view", { scene: this.scene, from: this.#enterFrom, cause: this.#enterCause });
    const derived: Occurrence[] = [];
    this.#enterScene(this.scene, derived, 0);
    this.#run([{ trigger: { type: "scene.enter", scene: this.scene }, depth: 0, gesture: false }, ...derived]);
    const next = this.#deferredGoTo;
    this.#deferredGoTo = null;
    // If scene.enter itself started another transition, #goTo defers this request again.
    if (next) this.#goTo(next.sceneId, next.transition, next.cause, 0);
    this.#changed();
  }

  #timerElapsed(id: string): void {
    const t = this.#timers.get(id);
    const decl = this.doc.timers?.find((x) => x.id === id);
    if (!t || !decl) return;
    t.cycles += 1;
    // `repeat` (owner decision, ADR-0020 §5 ST-O9): a number = TOTAL executions; `true` = explicit infinite.
    const repeat = decl.repeat ?? false;
    const again = repeat === true || (typeof repeat === "number" && t.cycles < repeat);
    t.ticks = new Map();
    if (again) t.cycleStart = this.now;
    else {
      t.running = false;
      t.pausedAt = 0;
    }
    this.#run([{ trigger: { type: "timer.elapsed", timer: id }, depth: 0, gesture: false }]);
    // The game's time limit ended (its single run): the declared win condition decides.
    const g = this.game;
    if (g?.running && g.decl.timer === id && !again) this.#endGame(g.timeUp(this.#readings()));
  }

  #runPending(p: Pending): void {
    this.#pending = this.#pending.filter((x) => x !== p);
    this.#exec(p.actions, p.depth, false, []);
  }

  // ─── Execution ────────────────────────────────────────────────────────────

  #isEnabled(r: Rule) {
    return this.#enabled.get(r.id) ?? r.enabled ?? true;
  }

  #reachable(r: Rule): boolean {
    const on = r.on as { target?: string; scene?: string };
    if (on.target !== undefined && r.on.type.startsWith("pointer.")) return this.isOnScreen(on.target);
    if (r.on.type === "timeline.at" || r.on.type === "scene.timelineEnd") return on.scene === this.scene;
    return true;
  }

  #env(): ExprEnvironment {
    return {
      variable: (id) => this.#vars.get(id),
      elementState: (id) => this.#states.get(id) ?? "",
      currentScene: () => this.scene,
      timer: (id, prop) => {
        const t = this.#timers.get(id);
        const decl = this.doc.timers?.find((x) => x.id === id);
        if (prop === "running") return t?.running ?? false;
        if (!decl) return 0;
        // A timer that never ran shows its full duration (e.g. a countdown before the game starts).
        if (!t) return decl.durationMs;
        const elapsed = t.running ? this.now - t.cycleStart : t.pausedAt;
        return Math.max(0, decl.durationMs - elapsed);
      },
      random: this.#random,
      fieldProp: (form, field, prop) => {
        const filled = this.#filled(form, field);
        if (prop === "filled") return filled;
        return filled || !this.#fieldDecl(form, field)?.required;
      },
      answered: (question, option) => {
        const a = this.#survey.answers.get(question);
        return a !== undefined && (option === undefined || (a.opts?.includes(option) ?? false));
      },
    };
  }

  /**
   * Processes occurrences breadth-first; `only` restricts the first one to specific rules.
   * One logical interaction (the outermost run: an input, a scheduled instant) executes at most
   * ENGINE_LIMITS.actionsPerInteraction actions and cascades at most cascadeDepth deep; the rest
   * is cut and reported once (runtime_error RULE_BUDGET / RULE_CASCADE_LIMIT). Never loops.
   */
  #run(initial: Occurrence[], only?: readonly Rule[]): void {
    if (this.#runDepth === 0) this.#actions = 0;
    this.#runDepth += 1;
    try {
      const queue: Occurrence[] = [...initial];
      let first = only !== undefined;
      while (queue.length > 0) {
        const occ = queue.shift() as Occurrence;
        if (occ.depth > ENGINE_LIMITS.cascadeDepth) {
          this.#report("RULE_CASCADE_LIMIT");
          continue;
        }
        if (this.#actions >= ENGINE_LIMITS.actionsPerInteraction) {
          this.#report("RULE_BUDGET");
          break;
        }
        const candidates = (first ? only : (this.doc.rules ?? []).filter((r) => sameTrigger(r.on, occ.trigger))) ?? [];
        first = false;
        for (const rule of candidates) {
          if (!this.#isEnabled(rule) || !this.#reachable(rule)) continue;
          if (rule.once && this.#onceFired.has(rule.id)) continue;
          const last = this.#lastFired.get(rule.id);
          if (rule.cooldownMs && last !== undefined && this.now - last < rule.cooldownMs) continue;
          if (rule.if !== undefined && evaluateExpr(rule.if, this.#env()) !== true) continue;
          this.#onceFired.add(rule.id);
          this.#lastFired.set(rule.id, this.now);
          const derived: Occurrence[] = [];
          this.#exec(rule.do, occ.depth, occ.gesture, derived, rule);
          queue.push(...derived);
        }
      }
    } finally {
      this.#runDepth -= 1;
    }
    // Run-to-completion step done: the game checks its end (win first, then lives).
    if (this.#runDepth === 0) {
      const g = this.game;
      const outcome = g?.running ? g.checkEnd(this.#readings()) : null;
      if (outcome) this.#endGame(outcome);
    }
    this.#changed();
  }

  /** Reports a runtime_error once per code and instance (bounded, never a flood). */
  #report(code: "RULE_BUDGET" | "RULE_CASCADE_LIMIT"): void {
    if (this.#reported.has(code)) return;
    this.#reported.add(code);
    this.#log("runtime_error", { code });
  }

  #exec(actions: readonly Action[], depth: number, gesture: boolean, derived: Occurrence[], rule?: Rule): void {
    for (const a of actions) {
      if (this.#actions >= ENGINE_LIMITS.actionsPerInteraction) {
        this.#report("RULE_BUDGET");
        return;
      }
      this.#actions += 1;
      switch (a.type) {
        case "scene.goTo":
          this.#goTo(a.scene, a.transition, "user", depth, derived);
          break;
        case "scene.next":
        case "scene.prev": {
          const i = this.doc.scenes.findIndex((s) => s.id === this.scene);
          const next = this.doc.scenes[i + (a.type === "scene.next" ? 1 : -1)];
          if (next) this.#goTo(next.id, a.transition, "user", depth, derived);
          break;
        }
        case "link.open": {
          if (!gesture) break; // the validator forbids it outside a gesture; never navigate without one
          const target = rule && "target" in rule.on ? rule.on.target : undefined;
          const el = target ? this.doc.elements.find((e) => e.id === target) : undefined;
          const source = el?.type === "hotspot" ? "hotspot" : el?.type === "button" ? "cta" : "element";
          this.#log("cta_click", { link: a.link, source });
          this.#onLink?.(a.link, this.doc.links.find((l) => l.id === a.link)?.url);
          break;
        }
        case "element.show":
        case "element.hide":
        case "element.toggle": {
          const p = this.#presentation?.elements[a.target] as { visible?: boolean } | undefined;
          const current = this.#visibility.get(a.target) ?? p?.visible !== false;
          this.#visibility.set(
            a.target,
            a.type === "element.show" ? true : a.type === "element.hide" ? false : !current
          );
          break;
        }
        case "element.setState":
          this.#states.set(a.target, a.state);
          break;
        case "animation.play":
          this.#manual.set(a.target, { ...(this.#manual.get(a.target) ?? {}), [a.track]: this.sceneTime() });
          break;
        case "animation.stop": {
          const rest = Object.entries(this.#manual.get(a.target) ?? {}).filter(([track]) => track !== a.track);
          this.#manual.set(a.target, Object.fromEntries(rest));
          break;
        }
        case "var.set":
        case "var.increment":
        case "var.reset": {
          const decl = this.doc.variables?.find((v) => v.id === a.var);
          if (!decl) break;
          const before = this.#vars.get(a.var);
          const value: ExprValue =
            a.type === "var.reset"
              ? decl.initial
              : a.type === "var.set"
                ? evaluateExpr(a.value, this.#env())
                : (typeof before === "number" ? before : 0) +
                  (() => {
                    const by = evaluateExpr(a.by, this.#env());
                    return typeof by === "number" ? by : 0;
                  })();
          this.#setVar(a.var, value, derived, depth);
          break;
        }
        case "random.choose": {
          const decl = this.doc.variables?.find((v) => v.id === a.var);
          if (decl?.type !== "enum") break;
          const from = a.from ?? decl.values;
          const weights = a.weights ?? from.map(() => 1);
          const total = weights.reduce((s, w) => s + w, 0);
          let r = this.#random() * total;
          let pick = from[from.length - 1] as string;
          for (let i = 0; i < from.length; i++) {
            r -= weights[i] ?? 0;
            if (r < 0) {
              pick = from[i] as string;
              break;
            }
          }
          const before = this.#vars.get(a.var);
          this.#vars.set(a.var, pick);
          if (pick !== before)
            derived.push({ trigger: { type: "variable.changed", var: a.var }, depth: depth + 1, gesture: false });
          break;
        }
        case "timer.start":
          this.#startTimer(a.timer);
          break;
        case "timer.stop": {
          const t = this.#timers.get(a.timer);
          if (t?.running) {
            t.running = false;
            t.pausedAt = this.now - t.cycleStart;
          }
          break;
        }
        case "timer.reset":
          this.#timers.set(a.timer, { running: false, cycleStart: this.now, pausedAt: 0, cycles: 0, ticks: new Map() });
          break;
        case "event.emit": {
          const decl = this.doc.events?.find((e) => e.name === a.name);
          if (!decl) break;
          const count = this.#customCount.get(a.name) ?? 0;
          if (count >= decl.maxPerInstance) break; // bounded per instance, as in production
          this.#customCount.set(a.name, count + 1);
          const props = Object.fromEntries(
            Object.entries(a.props ?? {}).map(([k, v]) => {
              const value = evaluateExpr(v, this.#env());
              return [k, typeof value === "boolean" ? String(value) : value];
            })
          );
          this.#log("custom", { name: a.name, ...props });
          break;
        }
        case "rule.enable":
        case "rule.disable":
          this.#enabled.set(a.rule, a.type === "rule.enable");
          break;
        case "after":
          // Bounded: a storm of deferred actions is cut and reported, never queued without limit.
          if (this.#pending.length >= ENGINE_LIMITS.pendingActions) {
            this.#report("RULE_BUDGET");
            break;
          }
          this.#pending.push({ due: this.now + a.ms, actions: a.do, scene: this.scene, seq: this.#seq++, depth });
          break;
        case "element.relocate":
          this.#relocate(a.target, a.area);
          break;
        case "game.start":
          this.#startGame(derived, depth);
          break;
        case "game.end": {
          const g = this.game;
          const outcome: GameOutcome =
            a.outcome === "auto" ? (g ? g.resolveAuto(this.#readings()) : "neutral") : a.outcome;
          this.#endGame(outcome, derived, depth);
          break;
        }
        case "game.restart":
          this.#restart(a.scene, derived, depth);
          break;
        case "media.play":
        case "media.pause":
        case "media.toggle":
        case "media.mute":
        case "media.unmute":
        case "media.restart":
          this.#mediaAction(a.target, a.type, gesture, derived, depth);
          break;
        case "component.command":
          this.#command(a.target, a.command, a.args?.index, derived, depth);
          break;
        case "survey.start":
          if (this.doc.survey?.id !== a.survey) break;
          if (this.scene !== this.doc.survey.presentation.sceneId)
            this.#goTo(this.doc.survey.presentation.sceneId, undefined, "survey", depth, derived);
          else this.#surveyStart();
          break;
        case "form.submit":
          this.#submit(a.form, gesture, derived, depth);
          break;
        case "form.reset":
          this.#forms.delete(a.form);
          break;
        default:
          this.unsupported.add(a.type);
      }
    }
  }

  #startTimer(id: string) {
    const t = this.#timers.get(id);
    if (t?.running) return;
    this.#timers.set(id, {
      running: true,
      cycleStart: this.now - (t?.pausedAt ?? 0),
      pausedAt: 0,
      cycles: t?.cycles ?? 0,
      // Resuming a stopped cycle keeps its ticks; a new cycle starts them again.
      ticks: t && t.pausedAt > 0 ? t.ticks : new Map<string, number>(),
    });
  }

  #goTo(
    sceneId: string,
    transition: Scene["transitionIn"] | undefined,
    cause: SceneCause,
    depth: number,
    derived?: Occurrence[]
  ): void {
    const target = this.doc.scenes.find((s) => s.id === sceneId);
    if (!target) return;
    if (this.transition) {
      this.#deferredGoTo = { sceneId, transition, cause };
      return;
    }
    const from = this.scene;
    const fromTime = this.sceneTime();
    const queue = derived ?? [];
    queue.push({ trigger: { type: "scene.exit", scene: from }, depth: depth + 1, gesture: false });
    // The outgoing scene stops being active NOW: its media stop and rewind, an unfinished survey is exited.
    this.#exitScene(from, queue, depth);
    this.#gameOnSceneLeave(sceneId);
    // Deferred actions of the scene being left are cancelled.
    this.#pending = this.#pending.filter((p) => p.scene !== from);
    this.scene = sceneId;
    this.sceneEnded = false;
    this.#timelineFired.clear();
    this.#manual.clear();
    const tr = transition ?? target.transitionIn;
    const animated = tr !== undefined && tr.kind !== "none" && tr.duration > 0;
    // The incoming timeline starts when it becomes active (transition completion).
    this.sceneStart = this.now + (animated ? tr.duration : 0);
    this.transition = animated
      ? {
          from,
          fromTime,
          kind: tr.kind,
          direction: tr.direction ?? "left",
          duration: tr.duration,
          easing: tr.easing,
          start: this.now,
        }
      : null;
    if (this.transition) {
      // scene_view + scene.enter happen at completion (#completeTransition).
      this.#enterFrom = from;
      this.#enterCause = cause;
    } else {
      this.#log("scene_view", { scene: sceneId, from, cause });
      queue.push({ trigger: { type: "scene.enter", scene: sceneId }, depth: depth + 1, gesture: false });
      this.#enterScene(sceneId, queue, depth);
    }
    if (!derived) this.#run(queue);
    this.#changed();
  }

  // ─── Game (7.6F): the declared module through the shared GameSession ────

  /** Sets a variable with its declared clamp/integer rule; a change is a derived variable.changed. */
  #setVar(id: string, raw: ExprValue, derived: Occurrence[], depth: number): void {
    const decl = this.doc.variables?.find((v) => v.id === id);
    if (!decl) return;
    const before = this.#vars.get(id);
    let value = raw;
    if (decl.type === "number" && typeof value === "number") {
      if (!Number.isFinite(value)) value = 0;
      if (decl.integer) value = Math.round(value);
      if (decl.min !== undefined) value = Math.max(decl.min, value);
      if (decl.max !== undefined) value = Math.min(decl.max, value);
    }
    this.#vars.set(id, value);
    if (value !== before)
      derived.push({ trigger: { type: "variable.changed", var: id }, depth: depth + 1, gesture: false });
  }

  #readings() {
    const g = this.game?.decl;
    const num = (id: string | undefined) => {
      const v = id === undefined ? undefined : this.#vars.get(id);
      return typeof v === "number" ? v : undefined;
    };
    return { score: num(g?.score) ?? 0, lives: num(g?.lives), energy: num(g?.runner?.energy?.var) };
  }

  /** Applies what the session decided, on this engine's clock and state. */
  #applyGame(effects: readonly GameEffect[], derived: Occurrence[], depth: number): void {
    const g = this.game;
    if (!g) return;
    for (const fx of effects)
      switch (fx.kind) {
        case "score":
        case "lives":
        case "energy": {
          const id =
            fx.kind === "score" ? g.decl.score : fx.kind === "lives" ? g.decl.lives : g.decl.runner?.energy?.var;
          if (id === undefined) break;
          const before = this.#vars.get(id);
          this.#setVar(id, (typeof before === "number" ? before : 0) + fx.delta, derived, depth);
          break;
        }
        case "state":
          this.#states.set(fx.target, fx.state);
          break;
        case "relocate":
          this.#relocate(fx.target, fx.area);
          break;
        case "settle":
          this.#settleAt = this.now + fx.ms;
          break;
        case "snap": {
          const f = this.#frame(fx.item);
          const z = this.frameNow(fx.zone);
          if (f && z) this.#positions.set(fx.item, snapToCenter(f, z));
          break;
        }
        case "return":
          this.#positions.delete(fx.item);
          break;
        case "signal":
          if (fx.event === "hazard") this.runnerStats.hazards += 1;
          if (fx.event === "pickup") this.runnerStats.pickups += 1;
          this.gameLog.push({ t: this.now, event: fx.event, ...(fx.target ? { target: fx.target } : {}) });
          if (this.gameLog.length > 50) this.gameLog.shift();
          derived.push({
            trigger: { type: "game.event", event: fx.event, ...(fx.target ? { target: fx.target } : {}) },
            depth: depth + 1,
            gesture: fx.gesture,
          });
          break;
        case "interaction":
          this.#countInteraction(fx.interaction);
          break;
      }
    this.#changed();
  }

  /** Random position fully inside the area's CURRENT frame in this variant (seeded RNG). */
  #relocate(target: string, area: string): void {
    const f = this.#frame(target);
    const a = this.frameNow(area);
    if (!f || !a) return;
    this.#positions.set(target, randomPlacement(f, a, this.#random));
  }

  /** game_interaction is aggregated per window and kind (never one event per tap). */
  #countInteraction(kind: "tap" | "drag"): void {
    // A window still open past its length (no `advance` ran since): close it first.
    if (this.#hasPendingInteractions() && this.now - this.#windowStart >= GAME_INTERACTION_WINDOW_MS)
      this.#flushInteractions();
    // The first counted interaction after a closed window opens the next one.
    if (!this.#hasPendingInteractions()) this.#windowStart = this.now;
    this.#interactions[kind] += 1;
  }
  #hasPendingInteractions(): boolean {
    return this.#interactions.tap > 0 || this.#interactions.drag > 0;
  }
  /**
   * Closes the open game_interaction window now: its counts become events (taxonomy unchanged)
   * and are zeroed, so a second call — or the window's own scheduled close — never repeats them.
   * The served ad calls it when the page is hidden, left or the ad destroyed (runtime 1.1.2):
   * interactions that really happened are reported without waiting for another tap.
   */
  flushPending(): void {
    this.#flushInteractions();
  }
  #flushInteractions(): void {
    for (const kind of ["tap", "drag"] as const) {
      const n = this.#interactions[kind];
      if (n > 0) this.#log("game_interaction", { kind, n: Math.min(n, 10_000) });
      this.#interactions[kind] = 0;
    }
  }

  #startGame(derived?: Occurrence[], depth = 0): void {
    const g = this.game;
    if (!g) {
      // Rule-only game (no module): game_start once per round.
      if (!this.#roundStarted) {
        this.#roundStarted = true;
        this.#log("game_start", {});
      }
      return;
    }
    if (g.phase !== "idle") return;
    const fx = g.start(this.#random);
    // Shuffled cards take the frames (slots) of other cards in this variant.
    for (const card of g.decl.pairs?.cards.flat() ?? []) {
      const slot = g.cardSlot(card);
      const target = slot !== card ? this.#frame(slot) : undefined;
      if (target) this.#positions.set(card, { x: target.x, y: target.y });
    }
    const timer = g.decl.timer;
    if (timer !== undefined) {
      this.#timers.set(timer, { running: false, cycleStart: this.now, pausedAt: 0, cycles: 0, ticks: new Map() });
      this.#startTimer(timer);
    }
    this.#gamePaused = false;
    this.#windowStart = this.now;
    this.#runnerNext = g.runner ? this.now + RUNNER_STEP_MS : null;
    this.#log("game_start", {});
    const queue = derived ?? [];
    this.#applyGame(fx, queue, depth);
    if (!derived) this.#run(queue);
  }

  /** Ends the round once: freezes every timer, flushes counts, measures game_complete, schedules the result. */
  #endGame(outcome: GameOutcome, derived?: Occurrence[], depth = 0): void {
    const g = this.game;
    if (!g) {
      if (this.#roundEnded) return;
      this.#roundEnded = true;
      this.#freezeTimers();
      const report = this.doc.variables?.find((v) => v.type === "number" && v.report === "onComplete");
      const score = report ? this.#vars.get(report.id) : undefined;
      this.#log("game_complete", { outcome, ...(typeof score === "number" ? { score: Math.round(score) } : {}) });
      return;
    }
    if (!g.running) return;
    const fx = g.end(outcome);
    this.#runnerNext = null;
    this.#settleAt = null;
    this.#picked = null;
    this.#freezeTimers();
    this.#flushInteractions();
    const score = g.decl.score !== undefined ? this.#vars.get(g.decl.score) : undefined;
    this.#log("game_complete", { outcome, ...(typeof score === "number" ? { score: Math.round(score) } : {}) });
    const result = g.resultScene(outcome);
    if (result !== undefined) this.#resultNav = { due: this.now + g.decl.results.delayMs, scene: result };
    const queue = derived ?? [];
    this.#applyGame(fx, queue, depth);
    if (!derived) this.#run(queue);
  }

  /** game.end freezes the round: every running timer stops where it is. */
  #freezeTimers(): void {
    for (const t of this.#timers.values())
      if (t.running) {
        t.running = false;
        t.pausedAt = this.now - t.cycleStart;
      }
  }

  /**
   * game.restart (INTERACTION-MODEL §7.4): variables and timers back to their declarations,
   * `once` rules re-armed and rules back to their declared enabled state, pending actions
   * cancelled, the game back to idle (found/matched/placed, positions and element states
   * undone), then go to `scene`. Same ad instance: a new round is never a new impression.
   * Visibility changed by show/hide is NOT reset (rules restore it explicitly).
   */
  #restart(scene: string, derived: Occurrence[], depth: number): void {
    this.#flushInteractions();
    this.#log("restart", {});
    for (const v of this.doc.variables ?? []) this.#vars.set(v.id, v.initial);
    this.#timers.clear();
    this.#onceFired.clear();
    this.#enabled.clear();
    this.#pending = [];
    this.#settleAt = null;
    this.#resultNav = null;
    this.#picked = null;
    this.#dragging = null;
    this.#positions.clear();
    this.#states.clear();
    for (const el of this.doc.elements) if (el.initialState) this.#states.set(el.id, el.initialState);
    this.game?.reset();
    this.#runnerNext = null;
    this.runnerStats.hazards = 0;
    this.runnerStats.pickups = 0;
    this.#gamePaused = false;
    this.#roundStarted = false;
    this.#roundEnded = false;
    // Declared state again: auto-start timers run from zero, as when the creative started.
    for (const t of this.doc.timers ?? []) if (t.autoStart) this.#startTimer(t.id);
    this.#goTo(scene, undefined, "game", depth, derived);
  }

  /** A scene became active: a game scene starts (onEnter) or resumes the round; others pause it. */
  #gameOnSceneActive(sceneId: string, queue: Occurrence[], depth: number): void {
    const g = this.game;
    if (!g) return;
    const inGame = g.decl.scenes.includes(sceneId);
    if (inGame && g.phase === "idle" && g.decl.start === "onEnter") this.#startGame(queue, depth);
    else if (inGame && g.running && this.#gamePaused) {
      this.#gamePaused = false;
      if (g.decl.timer !== undefined) this.#startTimer(g.decl.timer);
      // The run resumes where it was: no catch-up of the time spent away.
      if (g.runner) this.#runnerNext = this.now + RUNNER_STEP_MS;
    }
  }
  /** Leaving the game's scenes while it runs pauses its time limit (resumed on return). */
  #gameOnSceneLeave(nextScene: string): void {
    const g = this.game;
    if (!g?.running || g.decl.scenes.includes(nextScene) || this.#gamePaused) return;
    this.#gamePaused = true;
    this.#picked = null;
    this.game?.runnerDuck(false);
    const t = g.decl.timer !== undefined ? this.#timers.get(g.decl.timer) : undefined;
    if (t?.running) {
      t.running = false;
      t.pausedAt = this.now - t.cycleStart;
    }
  }

  // ─── Advanced components (7.6E) ─────────────────────────────────────────

  #element(id: string): Element | undefined {
    return this.doc.elements.find((e) => e.id === id);
  }

  /** Elements of a scene's subtree in this variant (top-level layers and what they contain). */
  #sceneSubtree(sceneId: string): Element[] {
    const out: Element[] = [];
    const visit = (id: string) => {
      const el = this.#element(id);
      if (!el || out.includes(el)) return;
      out.push(el);
      if (el.type === "group") el.children.forEach(visit);
      if (el.type === "component")
        Object.values(el.slots ?? {}).forEach((l) => {
          l.forEach(visit);
        });
    };
    (this.#layer.get(sceneId) ?? []).forEach(visit);
    return out;
  }

  /** In the active scene (or overlay) regardless of visibility: media keep playing while hidden. */
  #inActiveScene(id: string): boolean {
    let current = id;
    for (let guard = 0; guard < 64; guard++) {
      const up = this.#parents.get(current);
      if (!up) break;
      current = up.parent;
    }
    return (
      (this.#layer.get(this.scene) ?? []).includes(current) ||
      (this.#presentation?.overlay.below ?? []).includes(current) ||
      (this.#presentation?.overlay.above ?? []).includes(current)
    );
  }

  #isVideo(id: string): boolean {
    return this.#element(id)?.type === "video";
  }

  #duration(id: string): number {
    const el = this.#element(id);
    const key = el?.type === "video" || el?.type === "audio" ? el.asset : undefined;
    return Math.max(1, this.doc.assets.find((a) => a.key === key)?.durationMs ?? 1);
  }

  #position(m: MediaState, d: number): number {
    return m.playing ? Math.min(d, m.pos + (this.now - m.anchor)) : m.pos;
  }

  #mediaState(id: string): MediaState | undefined {
    const el = this.#element(id);
    if (el?.type !== "video" && el?.type !== "audio") return undefined;
    let m = this.#media.get(id);
    if (!m) {
      const pb = el.playback;
      m = {
        playing: false,
        // Default: muted when the intent is to autoplay (CREATIVE-MODEL §8).
        muted: pb.startMuted ?? pb.autoplay !== "none",
        volume: pb.volume ?? 1,
        pos: 0,
        anchor: this.now,
        started: false,
        fired: new Set(),
        completed: false,
        ended: false,
        blocked: false,
        loops: 0,
        pending: false,
        failed: false,
      };
      this.#media.set(id, m);
    }
    return m;
  }

  mediaView(id: string): MediaView | undefined {
    const m = this.#mediaState(id);
    if (!m) return undefined;
    const duration = this.#duration(id);
    return {
      playing: m.playing,
      muted: m.muted,
      volume: m.volume,
      position: this.#position(m, duration),
      duration,
      ended: m.ended,
      blocked: m.blocked,
      pending: m.pending,
      fresh: !m.started,
      failed: m.failed,
      ...(m.outcome ? { outcome: m.outcome } : {}),
    };
  }

  #play(id: string, m: MediaState, gesture: boolean, queue: Occurrence[], depth: number): void {
    // A media the browser cannot play never "plays" logically: no video_start, no milestones.
    if (m.playing || m.failed) return;
    m.pending = false;
    // Sound without a user gesture is refused by browsers: the outcome is muted playback.
    if (!gesture && !m.muted) {
      m.muted = true;
      m.outcome = "muted_instead_of_sound";
    }
    if (m.ended) {
      m.ended = false;
      m.pos = 0;
      m.loops = 0;
    }
    m.playing = true;
    m.blocked = false;
    m.anchor = this.now;
    if (!m.started) {
      m.started = true;
      this.#log(this.#isVideo(id) ? "video_start" : "audio_start", { el: id });
    }
    queue.push({ trigger: { type: "media.started", target: id }, depth: depth + 1, gesture: false });
  }

  #pause(id: string, m: MediaState, queue: Occurrence[], depth: number): void {
    m.pending = false;
    if (!m.playing) return;
    m.pos = this.#position(m, this.#duration(id));
    m.playing = false;
    queue.push({ trigger: { type: "media.paused", target: id }, depth: depth + 1, gesture: false });
  }

  #mediaAction(id: string, type: string, gesture: boolean, queue: Occurrence[], depth: number): void {
    const m = this.#mediaState(id);
    if (!m || !this.#inActiveScene(id)) return;
    const control = (action: "play" | "pause" | "mute" | "unmute" | "replay") => {
      if (gesture) this.#log("media_control", { el: id, media: this.#isVideo(id) ? "video" : "audio", action });
    };
    switch (type) {
      case "media.play":
        control("play");
        this.#play(id, m, gesture, queue, depth);
        break;
      case "media.pause":
        control("pause");
        this.#pause(id, m, queue, depth);
        break;
      case "media.toggle":
        control(m.playing || m.pending ? "pause" : "play");
        if (m.playing || m.pending) this.#pause(id, m, queue, depth);
        else this.#play(id, m, gesture, queue, depth);
        break;
      case "media.mute":
        control("mute");
        m.muted = true;
        break;
      case "media.unmute":
        // Only from a gesture (validated statically; enforced again here).
        if (!gesture) break;
        control("unmute");
        m.muted = false;
        delete m.outcome;
        break;
      case "media.restart":
        control("replay");
        m.pos = 0;
        m.ended = false;
        m.loops = 0;
        m.anchor = this.now;
        if (!m.playing) this.#play(id, m, gesture, queue, depth);
        break;
    }
  }

  /**
   * Served ad (mediaStart "confirmed"): the real element started playing. A pending autoplay
   * starts its logical playback NOW (video_start / audio_start, media.started); anything else
   * is ignored (the engine never reads the element back otherwise).
   */
  mediaPlaying(id: string): void {
    const m = this.#media.get(id);
    if (!m?.pending || !this.#inActiveScene(id)) return;
    const derived: Occurrence[] = [];
    this.#play(id, m, false, derived, 0);
    this.#run(derived);
  }

  /**
   * Served ad (mediaStart "confirmed", 7.6J): the real element cannot load or decode this media
   * (MediaError / NotSupportedError — e.g. a codec this browser lacks). Final for the instance:
   * the media stops being pending or playing, no media event is ever logged for it afterwards,
   * the poster stays. Never a trigger (no new taxonomy); the host reports it once as
   * runtime_error ASSET_LOAD (ADR-0029 §2.3, non-initial asset). Ignored in the preview.
   */
  mediaFailed(id: string): void {
    if (this.#mediaStart !== "confirmed") return;
    const m = this.#mediaState(id);
    if (!m || m.failed) return;
    if (m.playing) m.pos = this.#position(m, this.#duration(id));
    m.failed = true;
    m.pending = false;
    m.playing = false;
    m.blocked = false;
  }

  /** Served ad: the browser refused a pending autoplay (NotAllowedError) → tap-to-play, media.blocked. */
  mediaBlocked(id: string): void {
    const m = this.#media.get(id);
    if (!m?.pending) return;
    m.pending = false;
    m.blocked = true;
    m.outcome = "autoplay_blocked";
    this.#run([{ trigger: { type: "media.blocked", target: id }, depth: 0, gesture: false }]);
  }

  /** The preview's own media controls (minimal controls, tap-to-play): a user gesture. */
  mediaControl(id: string, action: "toggle" | "mute" | "unmute"): void {
    if (!this.isOnScreen(id) && !this.#inActiveScene(id)) return;
    this.#engage();
    const derived: Occurrence[] = [];
    this.#mediaAction(id, `media.${action}`, true, derived, 0);
    this.#run(derived);
  }

  #milestone(id: string, milestone: number): void {
    const m = this.#media.get(id);
    if (!m || m.fired.has(milestone)) return;
    m.fired.add(milestone);
    if (this.#isVideo(id)) this.#log(`video_${String(milestone)}` as PreviewEventType, { el: id });
    this.#run([
      {
        trigger: { type: "media.progress", target: id, milestone: milestone },
        depth: 0,
        gesture: false,
      },
    ]);
  }

  #mediaEnd(id: string): void {
    const m = this.#media.get(id);
    const el = this.#element(id);
    if (!m || (el?.type !== "video" && el?.type !== "audio")) return;
    const loop = el.playback.loop ?? false;
    const more = loop === true || (typeof loop === "object" && m.loops + 1 < loop.count);
    if (more) {
      m.loops += 1;
      m.pos = 0;
      m.anchor = this.now;
      this.#changed();
      return;
    }
    m.playing = false;
    m.pos = this.#duration(id);
    m.ended = true;
    if (!m.completed) {
      m.completed = true;
      for (const milestone of MILESTONES) m.fired.add(milestone);
      this.#log(this.#isVideo(id) ? "video_complete" : "audio_complete", { el: id });
    }
    if (el.type === "video" && el.playback.endBehavior === "hide") this.#visibility.set(id, false);
    this.#run([{ trigger: { type: "media.ended", target: id }, depth: 0, gesture: false }]);
  }

  #enterScene(sceneId: string, queue: Occurrence[], depth: number): void {
    for (const el of this.#sceneSubtree(sceneId)) {
      if (el.type === "component" && el.component === "ia.carousel@1") {
        const props = this.#carouselProps(el.id);
        const viewed = this.#carousels.get(el.id)?.viewed ?? new Set<number>();
        this.#carousels.set(el.id, {
          index: 0,
          since: this.now,
          autoplay: (props?.autoAdvanceMs ?? 0) > 0,
          held: false,
          paused: false,
          viewed,
        });
        if (!viewed.has(0)) {
          viewed.add(0);
          this.#log("carousel_slide_view", { el: el.id, index: 0, cause: "auto" });
        }
      }
      if (el.type === "video" || el.type === "audio") {
        const m = this.#mediaState(el.id);
        const pb = el.playback;
        if (!m || pb.autoplay === "none" || m.failed) continue;
        if (this.#autoplay === "block") {
          m.blocked = true;
          m.outcome = "autoplay_blocked";
          queue.push({ trigger: { type: "media.blocked", target: el.id }, depth: depth + 1, gesture: false });
          continue;
        }
        // Autoplay is always muted; asking for sound is degraded, never forced.
        m.muted = true;
        if (pb.autoplay === "withSound") m.outcome = "muted_instead_of_sound";
        // Served ad: the browser decides; the logical playback starts when it really starts.
        if (this.#mediaStart === "confirmed") m.pending = true;
        else this.#play(el.id, m, false, queue, depth);
      }
    }
    if (this.doc.survey?.presentation.sceneId === sceneId) this.#surveyStart();
    this.#gameOnSceneActive(sceneId, queue, depth);
  }

  #exitScene(sceneId: string, queue: Occurrence[], depth: number): void {
    for (const el of this.#sceneSubtree(sceneId)) {
      const m = el.type === "video" || el.type === "audio" ? this.#media.get(el.id) : undefined;
      if (m) {
        m.playing = false;
        m.pos = 0;
        m.ended = false;
        m.blocked = false;
        m.loops = 0;
        m.pending = false;
      }
    }
    const s = this.#survey;
    if (this.doc.survey?.presentation.sceneId === sceneId && s.started && !s.completed)
      queue.push({ trigger: { type: "survey.exited", survey: this.doc.survey.id }, depth: depth + 1, gesture: false });
  }

  // Carousel

  #carouselProps(id: string): CarouselProps | undefined {
    const el = this.#element(id);
    return el?.type === "component" && el.component === "ia.carousel@1"
      ? (el.props as unknown as CarouselProps)
      : undefined;
  }

  #slideCount(id: string): number {
    const el = this.#element(id);
    return el?.type === "component" ? (el.slots?.slides?.length ?? 0) : 0;
  }

  carouselView(id: string): CarouselView {
    const c = this.#carousels.get(id);
    return {
      index: c?.index ?? 0,
      count: this.#slideCount(id),
      autoplay: c?.autoplay ?? false,
      paused: c?.paused ?? false,
    };
  }

  #setSlide(
    id: string,
    target: number,
    cause: "auto" | "user",
    via: "swipe" | "arrow" | "dot" | "rule" | null,
    queue: Occurrence[],
    depth: number
  ): void {
    const c = this.#carousels.get(id);
    const count = this.#slideCount(id);
    if (!c || count === 0) return;
    const index = Math.max(0, Math.min(count - 1, target));
    if (cause === "user") c.autoplay = false; // the first user navigation stops auto-advance for good
    c.since = this.now;
    if (index === c.index) return;
    c.index = index;
    if (via) this.#log("carousel_navigate", { el: id, index, via });
    if (!c.viewed.has(index)) {
      c.viewed.add(index);
      this.#log("carousel_slide_view", { el: id, index, cause });
    }
    queue.push({
      trigger: { type: "component.event", target: id, event: "slideChanged" },
      depth: depth + 1,
      gesture: false,
    });
  }

  #step(id: string, delta: 1 | -1): number {
    const c = this.carouselView(id);
    const loop = this.#carouselProps(id)?.loop ?? false;
    const next = c.index + delta;
    if (next >= c.count) return loop ? 0 : c.count - 1;
    if (next < 0) return loop ? c.count - 1 : 0;
    return next;
  }

  #autoAdvance(id: string): void {
    const c = this.#carousels.get(id);
    if (!c) return;
    const loop = this.#carouselProps(id)?.loop ?? false;
    if (c.index + 1 >= this.#slideCount(id) && !loop) {
      c.autoplay = false;
      this.#changed();
      return;
    }
    const derived: Occurrence[] = [];
    this.#setSlide(id, this.#step(id, 1), "auto", null, derived, 0);
    this.#run(derived);
  }

  #command(id: string, command: string, index: number | undefined, queue: Occurrence[], depth: number): void {
    if (!this.#carousels.has(id) || !this.isOnScreen(id)) return;
    const target = command === "next" ? this.#step(id, 1) : command === "prev" ? this.#step(id, -1) : (index ?? 0);
    this.#setSlide(id, target, "user", "rule", queue, depth);
  }

  /** The carousel's own controls (arrows, dots, swipe): a user navigation. */
  carouselNavigate(id: string, to: "next" | "prev" | number, via: "swipe" | "arrow" | "dot"): void {
    if (!this.isOnScreen(id) || !this.#carousels.has(id)) return;
    this.#engage();
    const derived: Occurrence[] = [];
    const target = to === "next" ? this.#step(id, 1) : to === "prev" ? this.#step(id, -1) : to;
    this.#setSlide(id, target, "user", via, derived, 0);
    this.#run(derived);
  }

  /** Hover/focus inside the carousel holds auto-advance; leaving restarts the interval. */
  carouselHold(id: string, held: boolean): void {
    const c = this.#carousels.get(id);
    if (!c || c.held === held) return;
    c.held = held;
    if (!held) c.since = this.now;
    this.#changed();
  }

  carouselTogglePause(id: string): void {
    const c = this.#carousels.get(id);
    if (!c) return;
    c.paused = !c.paused;
    if (!c.paused) c.since = this.now;
    this.#changed();
  }

  // Survey

  surveyView(): SurveyView {
    const s = this.#survey;
    const question = s.current ? (this.doc.survey?.questions.find((q) => q.id === s.current) ?? null) : null;
    return {
      started: s.started,
      completed: s.completed,
      question,
      order: s.order,
      position: s.path.length,
      remaining: question ? this.#remaining(question.id) : 0,
      selection: s.selection,
      value: s.value,
    };
  }

  /** Longest path from a question to the end, inclusive (progress denominator; graph is acyclic). */
  #remaining(id: string, seen: ReadonlySet<string> = new Set()): number {
    const q = this.doc.survey?.questions.find((x) => x.id === id);
    if (!q || seen.has(id)) return 0;
    const next = new Set<string>(q.kind === "single" ? q.options.map((o) => o.next ?? q.next ?? SURVEY_END) : [q.next]);
    let best = 0;
    for (const n of next) if (n !== SURVEY_END) best = Math.max(best, this.#remaining(n, new Set([...seen, id])));
    return best + 1;
  }

  #surveyStart(): void {
    const survey = this.doc.survey;
    const s = this.#survey;
    if (!survey || s.started) return;
    s.started = true;
    this.#log("survey_start", {});
    this.#showQuestion(survey.startQuestionId);
  }

  #showQuestion(id: string): void {
    const s = this.#survey;
    const q = this.doc.survey?.questions.find((x) => x.id === id);
    if (!q) return;
    s.current = id;
    s.path.push(id);
    s.selection = [];
    s.value = null;
    const order = q.kind === "scale" ? [] : q.options.map((o) => o.id);
    if (q.kind !== "scale" && q.shuffleOptions)
      for (let i = order.length - 1; i > 0; i--) {
        const j = Math.floor(this.#random() * (i + 1));
        [order[i], order[j]] = [order[j] as string, order[i] as string];
      }
    s.order = order;
    this.#log("survey_question_view", { q: id, pos: s.path.length });
    this.#changed();
  }

  #surveyQuestion(): SurveyQuestion | undefined {
    const id = this.#survey.current;
    return id ? this.doc.survey?.questions.find((q) => q.id === id) : undefined;
  }

  #surveyAutoAdvance(): boolean {
    const el = this.doc.elements.find((e) => e.type === "component" && e.component === "ia.survey-question@1");
    return el?.type === "component" ? (el.props as { autoAdvance?: boolean }).autoAdvance !== false : true;
  }

  /** Participant picks an option (single/multi) or a scale value. */
  surveyChoose(choice: string | number): void {
    const q = this.#surveyQuestion();
    const s = this.#survey;
    if (!q || s.completed || this.transition || this.scene !== this.doc.survey?.presentation.sceneId) return;
    // Only what the question declares (a tampered control can never inject another value).
    if (
      q.kind === "scale"
        ? typeof choice !== "number" || !Number.isFinite(choice)
        : !q.options.some((o) => o.id === choice)
    )
      return;
    this.#engage();
    if (q.kind === "scale" && typeof choice === "number")
      s.value = Math.max(q.scale.min, Math.min(q.scale.max, Math.round(choice)));
    else if (q.kind === "single" && typeof choice === "string") s.selection = [choice];
    else if (q.kind === "multi" && typeof choice === "string") {
      if (s.selection.includes(choice)) s.selection = s.selection.filter((x) => x !== choice);
      else if (s.selection.length < q.maxSelections) s.selection = [...s.selection, choice];
    }
    this.#changed();
    if (q.kind !== "multi" && this.#surveyAutoAdvance()) this.surveyNext();
  }

  /** Whether the current answer may be committed ("Siguiente"). */
  surveyCanContinue(): boolean {
    const q = this.#surveyQuestion();
    const s = this.#survey;
    if (!q) return false;
    const answered = q.kind === "scale" ? s.value !== null : s.selection.length > 0;
    return answered || (!q.required && q.next !== undefined);
  }

  surveyNext(): void {
    const q = this.#surveyQuestion();
    const s = this.#survey;
    const survey = this.doc.survey;
    if (!q || !survey || !this.surveyCanContinue()) return;
    const answered = q.kind === "scale" ? s.value !== null : s.selection.length > 0;
    const pos = s.path.length;
    const derived: Occurrence[] = [];
    if (answered) {
      const answer = q.kind === "scale" ? { value: s.value ?? 0 } : { opts: [...s.selection] };
      s.answers.set(q.id, answer);
      this.#log("answer", { q: q.id, pos, ...("opts" in answer ? { opts: answer.opts } : { value: answer.value }) });
      derived.push({
        trigger: { type: "survey.answered", survey: survey.id, question: q.id },
        depth: 0,
        gesture: false,
      });
    }
    const next =
      q.kind === "single" && answered ? (q.options.find((o) => o.id === s.selection[0])?.next ?? q.next) : q.next;
    if (next === undefined || next === SURVEY_END) {
      s.completed = true;
      s.current = null;
      this.#log("survey_complete", { pathLength: pos });
      derived.push({ trigger: { type: "survey.completed", survey: survey.id }, depth: 0, gesture: false });
      this.#goTo(survey.presentation.completionSceneId, undefined, "survey", 0, derived);
    } else this.#showQuestion(next);
    this.#run(derived);
  }

  // Forms

  #form(formId: string): FormState {
    let f = this.#forms.get(formId);
    if (!f) {
      f = { started: false, values: new Map(), completed: new Set(), errors: new Set() };
      this.#forms.set(formId, f);
    }
    return f;
  }

  #fieldDecl(formId: string, fieldId: string) {
    return this.doc.forms?.find((f) => f.id === formId)?.fields.find((f) => f.id === fieldId);
  }

  #filled(formId: string, fieldId: string): boolean {
    const v = this.#forms.get(formId)?.values.get(fieldId);
    return v !== undefined && (Array.isArray(v) ? v.length > 0 : typeof v === "string" ? v.trim() !== "" : true);
  }

  fieldValue(formId: string, fieldId: string): string[] | number | string | undefined {
    return this.#forms.get(formId)?.values.get(fieldId);
  }

  fieldError(formId: string, fieldId: string): boolean {
    return this.#forms.get(formId)?.errors.has(fieldId) ?? false;
  }

  /** The participant changes a field (option ids, a scale value; local text never leaves the preview). */
  fieldInput(elementId: string, value: string[] | number | string): void {
    const el = this.#element(elementId);
    if (el?.type !== "field" || !this.isOnScreen(elementId)) return;
    const decl = this.#fieldDecl(el.form, el.field);
    if (!decl) return;
    const clean = fieldValueOf(decl, value);
    if (clean === undefined) return;
    value = clean;
    const f = this.#form(el.form);
    this.#engage();
    if (!f.started) {
      f.started = true;
      this.#log("form_start", { form: el.form });
    }
    f.values.set(el.field, value);
    f.errors.delete(el.field);
    if (this.#filled(el.form, el.field) && !f.completed.has(el.field)) {
      f.completed.add(el.field);
      this.#log("form_interaction", { form: el.form, field: el.field, action: "complete" });
    }
    this.#changed();
  }

  #submit(formId: string, gesture: boolean, queue: Occurrence[], depth: number): void {
    const decl = this.doc.forms?.find((f) => f.id === formId);
    if (!decl) return;
    const f = this.#form(formId);
    const missing = decl.fields.filter((fd) => fd.required && !this.#filled(formId, fd.id)).map((fd) => fd.id);
    if (missing.length > 0) {
      f.errors = new Set(missing);
      this.#log("form_submit", { form: formId, outcome: "invalid" });
      queue.push({ trigger: { type: "form.invalid", form: formId }, depth: depth + 1, gesture: false });
      return;
    }
    // Only closed answers are measurable: option ids and scale values (never local text).
    const choices: string[] = [];
    const ratings: Record<string, number> = {};
    for (const fd of decl.fields) {
      if (fd.dataClass !== "choice") continue;
      const v = f.values.get(fd.id);
      if (fd.control === "rating" && typeof v === "number") ratings[fd.id] = v;
      else if (Array.isArray(v)) choices.push(...v);
      else if (typeof v === "string" && v !== "") choices.push(v);
    }
    f.errors = new Set();
    this.#log("form_submit", {
      form: formId,
      outcome: "valid",
      ...(choices.length > 0 ? { choices } : {}),
      ...(Object.keys(ratings).length > 0 ? { ratings } : {}),
    });
    queue.push({ trigger: { type: "form.submitted", form: formId }, depth: depth + 1, gesture });
  }

  #engage() {
    if (this.#engaged) return;
    this.#engaged = true;
    this.#log("engagement", {});
  }

  #log(type: PreviewEventType, attrs: PreviewEvent["attrs"]) {
    this.events.push({ t: this.now, type, attrs });
    if (this.events.length > 500) this.events.shift();
  }

  #changed() {
    this.version += 1;
  }
}

/** The Studio's name for the same engine (7.6D–7.6G); kept so authoring code and tests are unchanged. */
export { InteractionEngine as PreviewEngine };
